/**
 * Phase 20 Plan 20-02 — AlertDetailDialog
 *
 * Shows full alert details: severity badge, type, message, related entity
 * link, and audit trail rows (acknowledgedBy / dismissedBy + timestamps).
 *
 * Client feedback (2026-09): the message rendered in a plain <p>, so the composer's
 * multi-line bodies ("…rejected by X.\nReason: …\nRemarks: …") collapsed into one
 * run-on line and the Reason/Remarks detail was effectively lost. `whitespace-pre-line`
 * restores the line structure, and the raw enum name is replaced by a readable label.
 */

import type { JSX } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { notificationTypeLabel } from '@/lib/utils/notification-label';
import { formatRelative } from '@/lib/utils/relative-time';
import type { AlertDto, AlertSeverity } from '@/types/notifications';

interface AlertDetailDialogProps {
  readonly open: boolean;
  readonly alert: AlertDto;
  readonly onClose: () => void;
}

function severityVariant(s: AlertSeverity): 'default' | 'secondary' | 'destructive' {
  if (s === 'Critical') return 'destructive';
  if (s === 'Warning') return 'secondary';
  return 'default';
}

export function AlertDetailDialog({ open, alert, onClose }: AlertDetailDialogProps): JSX.Element {
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="alert-detail-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Badge
              variant={severityVariant(alert.severity)}
              data-testid="alert-severity-badge"
            >
              {alert.severity}
            </Badge>
            <span data-testid="alert-type-label" className="text-sm font-normal text-muted-foreground">
              {notificationTypeLabel(alert.notificationType)}
            </span>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Message */}
          <p className="whitespace-pre-line text-sm" data-testid="alert-message">
            {alert.message}
          </p>

          {/* Related entity */}
          {alert.relatedEntityId !== null && alert.relatedEntityType !== null && (
            <p className="text-xs text-muted-foreground" data-testid="alert-related-entity">
              Related: {alert.relatedEntityType} #{String(alert.relatedEntityId)}
            </p>
          )}

          {/* Created at */}
          <p className="text-xs text-muted-foreground">
            Created: {new Date(alert.createdAt).toLocaleString()} ({formatRelative(alert.createdAt)})
          </p>

          {/* Audit trail */}
          {alert.acknowledgedByUserId !== null && alert.acknowledgedAt !== null && (
            <div
              className="rounded-md border bg-muted/40 p-3 text-xs space-y-1"
              data-testid="alert-acknowledged-row"
            >
              <p className="font-medium">Acknowledged</p>
              <p>By user #{String(alert.acknowledgedByUserId)}</p>
              <p>{new Date(alert.acknowledgedAt).toLocaleString()} ({formatRelative(alert.acknowledgedAt)})</p>
            </div>
          )}

          {alert.dismissedByUserId !== null && alert.dismissedAt !== null && (
            <div
              className="rounded-md border bg-muted/40 p-3 text-xs space-y-1"
              data-testid="alert-dismissed-row"
            >
              <p className="font-medium">Dismissed</p>
              <p>By user #{String(alert.dismissedByUserId)}</p>
              <p>{new Date(alert.dismissedAt).toLocaleString()} ({formatRelative(alert.dismissedAt)})</p>
            </div>
          )}
        </div>

        <div className="flex justify-end">
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
